import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, CalendarDays, Search, ArrowLeft, Filter, ArrowUpDown, Archive, Trash2, Edit, FileText, CreditCard, DollarSign, Receipt, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useTeamPermissions } from '../contexts/TeamPermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Card', 'Check', 'Other'];
const PAYMENT_STATUSES = ['Pending', 'Partial', 'Paid', 'Overdue', 'Refunded', 'Cancelled'];

const Bookings = () => {
  const { t } = useTranslation();
  const { canEdit } = useTeamPermissions();
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [documentStatus, setDocumentStatus] = useState({});
  
  // Edit booking state
  const [editingBooking, setEditingBooking] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // View booking state (with payments)
  const [viewingBooking, setViewingBooking] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('details');
  
  // Payment tracking state
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer'
  });
  
  // Payment date dialog state
  const [showPaymentDateDialog, setShowPaymentDateDialog] = useState(false);
  const [paymentDateBookingId, setPaymentDateBookingId] = useState(null);
  const [paymentDate, setPaymentDate] = useState('');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPackageId, setFilterPackageId] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Package selection filters in form
  const [packageSearchTerm, setPackageSearchTerm] = useState('');
  const [packageFilterCurrency, setPackageFilterCurrency] = useState('all');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedCostElementId, setSelectedCostElementId] = useState('');
  
  const [formData, setFormData] = useState({
    package_id: '',
    cost_element_id: '',
    date_of_booking: '',
    date_of_service: '',
    booking_cost: '',
    service_provider: '',
    payment_method: '',
    payment_date: '',
    payment_status: 'Pending',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [bookingsRes, packagesRes, activePackagesRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/packages`, { headers }),
        axios.get(`${API}/packages/active`, { headers })
      ]);
      
      setBookings(bookingsRes.data);
      setPackages(packagesRes.data);
      setActivePackages(activePackagesRes.data);
      
      // Fetch document status for each booking
      const statusPromises = bookingsRes.data.map(booking =>
        axios.get(`${API}/bookings/${booking.booking_id}/document-status`, { headers })
          .then(res => ({ booking_id: booking.booking_id, status: res.data }))
          .catch(() => ({ booking_id: booking.booking_id, status: {} }))
      );
      
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses.forEach(s => {
        statusMap[s.booking_id] = s.status;
      });
      setDocumentStatus(statusMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Please verify your email to access this feature');
      }
    } finally {
      setLoading(false);
    }
  };

  // Payment tracking functions for service provider payments
  const fetchBookingPaymentData = async (bookingId) => {
    if (!bookingId) return;
    
    setLoadingPayments(true);
    try {
      const token = localStorage.getItem('auth_token');
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API}/payments/bookings/${bookingId}/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payments/bookings/${bookingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPaymentSummary(summaryRes.data);
      setPaymentHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      // Set default values if no payments exist yet
      const bookingCost = viewingBooking?.booking_cost || 0;
      setPaymentSummary({
        total_amount: bookingCost,
        amount_paid: 0,
        balance_due: bookingCost,
        payment_status: 'Pending',
        payment_count: 0,
        currency: viewingBooking?.currency || 'EUR'
      });
      setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleAddBookingPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setAddingPayment(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/payments/bookings/${viewingBooking.booking_id}`,
        {
          amount: parseFloat(paymentForm.amount),
          payment_date: paymentForm.payment_date,
          payment_method: paymentForm.payment_method
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment to service provider recorded');
      setShowAddPaymentDialog(false);
      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Bank Transfer'
      });
      
      // Refresh payment data for the modal
      fetchBookingPaymentData(viewingBooking.booking_id);
      // Also refresh the main bookings list to update payment status in table
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setAddingPayment(false);
    }
  };

  const handleDeleteBookingPayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${API}/payments/bookings/${viewingBooking.booking_id}/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment deleted');
      fetchBookingPaymentData(viewingBooking.booking_id);
      // Also refresh the main bookings list to update payment status in table
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Partial': return 'bg-yellow-100 text-yellow-800';
      case 'Pending': return 'bg-gray-100 text-gray-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      case 'Refunded': return 'bg-purple-100 text-purple-800';
      case 'Cancelled': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Fetch payment data when viewing booking changes
  useEffect(() => {
    if (viewingBooking) {
      setActiveDetailTab('details');
      fetchBookingPaymentData(viewingBooking.booking_id);
    }
  }, [viewingBooking?.booking_id]);

  // Filter active packages for dropdown
  const filteredPackages = activePackages.filter(pkg => {
    const matchesSearch = pkg.client_name?.toLowerCase().includes(packageSearchTerm.toLowerCase()) ||
                         pkg.package_id?.toLowerCase().includes(packageSearchTerm.toLowerCase());
    const matchesCurrency = packageFilterCurrency === 'all' || pkg.currency === packageFilterCurrency;
    return matchesSearch && matchesCurrency;
  });

  // Get unique currencies from active packages
  const availableCurrencies = [...new Set(activePackages.map(p => p.currency))];

  // Filter and sort bookings for display
  const filteredBookings = bookings
    .filter(booking => {
      const pkg = packages.find(p => p.package_id === booking.package_id);
      const matchesSearch = 
        booking.booking_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.package_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.service_provider?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg?.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPackage = filterPackageId === 'all' || booking.package_id === filterPackageId;
      return matchesSearch && matchesPackage;
    })
    .sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'booking_cost' || sortField === 'service_value') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handlePackageSelect = (packageId) => {
    const pkg = activePackages.find(p => p.package_id === packageId);
    setSelectedPackage(pkg);
    setFormData({ ...formData, package_id: packageId, cost_element_id: '' });
    setSelectedCostElementId('');
  };

  // Get cost elements with their total booked amounts
  const getCostElementsWithBookedAmounts = () => {
    if (!selectedPackage?.use_bottom_up || !selectedPackage?.cost_elements) return [];
    
    return selectedPackage.cost_elements
      .filter(elem => !['Contingency', 'Profit'].includes(elem.expense_type))
      .map(elem => {
        // Calculate total booked for this element
        const elementBookings = bookings.filter(b => 
          b.package_id === selectedPackage.package_id && 
          b.cost_element_id === elem.element_id
        );
        const totalBooked = elementBookings.reduce((sum, b) => sum + (b.booking_cost || b.service_value || 0), 0);
        
        return {
          ...elem,
          total_booked: totalBooked,
          booking_count: elementBookings.length
        };
      });
  };

  const resetForm = () => {
    setFormData({
      package_id: '',
      cost_element_id: '',
      date_of_booking: '',
      date_of_service: '',
      booking_cost: '',
      service_provider: '',
      payment_method: '',
      payment_date: '',
      payment_status: 'Pending',
    });
    setSelectedPackage(null);
    setSelectedCostElementId('');
    setPackageSearchTerm('');
    setPackageFilterCurrency('all');
    setShowCreateForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      
      const payload = {
        package_id: formData.package_id,
        date_of_booking: formData.date_of_booking,
        date_of_service: formData.date_of_service,
        service_value: parseFloat(formData.booking_cost), // Store as service_value for compatibility
        booking_cost: parseFloat(formData.booking_cost),
        service_provider: formData.service_provider,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date || null,
        payment_status: formData.payment_status,
        cost_element_id: selectedPackage?.use_bottom_up ? selectedCostElementId : null,
      };

      await axios.post(
        `${API}/bookings`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking created successfully!');
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  // Show payment date dialog when marking as paid
  const handleMarkPaidClick = (bookingId) => {
    setPaymentDateBookingId(bookingId);
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setShowPaymentDateDialog(true);
  };

  // Confirm payment with date
  const handleConfirmPayment = async () => {
    if (!paymentDate) {
      toast.error('Please select a payment date');
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(
        `${API}/bookings/${paymentDateBookingId}`,
        null,
        {
          params: { payment_status: 'Paid', payment_date: paymentDate },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Booking marked as paid!');
      setShowPaymentDateDialog(false);
      setPaymentDateBookingId(null);
      setPaymentDate('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Edit booking
  const handleEditBooking = (booking) => {
    setEditingBooking({
      ...booking,
      booking_cost: booking.booking_cost || booking.service_value || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API}/bookings/${editingBooking.booking_id}`,
        {
          date_of_booking: editingBooking.date_of_booking,
          date_of_service: editingBooking.date_of_service,
          service_provider: editingBooking.service_provider,
          booking_cost: parseFloat(editingBooking.booking_cost),
          payment_method: editingBooking.payment_method,
          payment_status: editingBooking.payment_status,
          payment_date: editingBooking.payment_date || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking updated successfully!');
      setShowEditDialog(false);
      setEditingBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const handleArchiveBooking = async (bookingId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(`${API}/bookings/${bookingId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive booking');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/bookings/${bookingId}/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking moved to trash');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const getPackageInfo = (packageId) => {
    return packages.find(p => p.package_id === packageId);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get document status indicator - now shows both Invoice and Receipt separately
  const getDocStatusIndicator = (booking) => {
    const status = documentStatus[booking.booking_id] || {};
    const hasInvoice = status.has_supplier_invoice;
    const hasReceipt = status.has_payment_receipt;
    
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <span className={`w-2 h-2 rounded-full ${hasInvoice ? 'bg-green-500' : 'bg-red-400'}`}></span>
          <span className="text-xs">{t('bookings.supplierInvoice')}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className={`w-2 h-2 rounded-full ${hasReceipt ? 'bg-green-500' : 'bg-red-400'}`}></span>
          <span className="text-xs">{t('bookings.paymentReceipt')}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  // Full-page booking creation form
  if (showCreateForm) {
    const costElementsWithBookings = getCostElementsWithBookedAmounts();
    const selectedElement = costElementsWithBookings.find(e => e.element_id === selectedCostElementId);
    
    return (
      <div className="space-y-6" data-testid="booking-create-form">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={resetForm}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('bookings.backToBookings')}
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-extrabold text-foreground">{t('bookings.createNewBooking')}</h1>
            <p className="text-muted-foreground">{t('bookings.linkBookingToPackage')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Step 1: Package Selection */}
          <Card>
            <CardHeader>
              <CardTitle>{t('bookings.step1SelectPackage')}</CardTitle>
              <CardDescription>{t('bookings.onlyActivePackages')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('bookings.searchByClient')}
                    value={packageSearchTerm}
                    onChange={(e) => setPackageSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={packageFilterCurrency} onValueChange={setPackageFilterCurrency}>
                  <SelectTrigger className="w-32">
                    <Filter className="w-4 h-4 mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bookings.allCurrencies')}</SelectItem>
                    {availableCurrencies.map(curr => (
                      <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Select value={formData.package_id} onValueChange={handlePackageSelect} required>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('bookings.selectPackage')} />
                </SelectTrigger>
                <SelectContent>
                  {filteredPackages.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      {t('bookings.noActivePackages')}
                    </div>
                  ) : (
                    filteredPackages.map((pkg) => (
                      <SelectItem key={pkg.package_id} value={pkg.package_id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{pkg.package_id}</span>
                          <span>- {pkg.client_name}</span>
                          <Badge variant="outline" className="ml-1">{pkg.currency}</Badge>
                          {pkg.use_bottom_up && <Badge variant="secondary" className="text-xs">{t('packages.bottomUp')}</Badge>}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>

              {/* Selected Package Details */}
              {selectedPackage && (
                <div className="p-4 bg-muted/50 rounded-lg border space-y-2">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('packages.clientName')}:</span>
                      <p className="font-semibold">{selectedPackage.client_name}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('packages.saleValue')}:</span>
                      <p className="font-semibold text-green-600">{selectedPackage.currency} {selectedPackage.sale_value?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('bookings.plannedCost')}:</span>
                      <p className="font-semibold">{selectedPackage.currency} {selectedPackage.expected_cost?.toLocaleString()}</p>
                    </div>
                  </div>
                  {selectedPackage.use_bottom_up && (
                    <Badge className="mt-2">{t('bookings.bottomUpPackageSelect')}</Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Cost Element Selection (for Bottom-Up packages) */}
          {selectedPackage?.use_bottom_up && costElementsWithBookings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('bookings.step2SelectCostElement')}</CardTitle>
                <CardDescription>{t('bookings.costElementDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-12">{t('common.select')}</TableHead>
                      <TableHead>{t('bookings.elementId')}</TableHead>
                      <TableHead>{t('packages.expenseType')}</TableHead>
                      <TableHead>{t('packages.serviceProvider')}</TableHead>
                      <TableHead>{t('packages.location')}</TableHead>
                      <TableHead className="text-right bg-blue-50">{t('bookings.estimatedFromSales')}</TableHead>
                      <TableHead className="text-right bg-green-50">{t('bookings.totalBooked')}</TableHead>
                      <TableHead className="text-right">{t('bookings.bookingsCount')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costElementsWithBookings.map((elem) => (
                      <TableRow 
                        key={elem.element_id} 
                        className={`cursor-pointer ${selectedCostElementId === elem.element_id ? 'bg-primary/10 border-l-4 border-l-primary' : 'hover:bg-muted/50'}`}
                        onClick={() => {
                          setSelectedCostElementId(elem.element_id);
                          setFormData({ ...formData, cost_element_id: elem.element_id });
                        }}
                      >
                        <TableCell>
                          <input
                            type="radio"
                            name="cost_element"
                            checked={selectedCostElementId === elem.element_id}
                            onChange={() => {
                              setSelectedCostElementId(elem.element_id);
                              setFormData({ ...formData, cost_element_id: elem.element_id });
                            }}
                            className="w-4 h-4"
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{elem.element_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{elem.expense_type}</Badge>
                        </TableCell>
                        <TableCell>{elem.service_provider || '-'}</TableCell>
                        <TableCell>{elem.location || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-blue-600 bg-blue-50/50">
                          {selectedPackage.currency} {elem.value?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600 bg-green-50/50">
                          {selectedPackage.currency} {elem.total_booked?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{elem.booking_count}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {selectedElement && (
                  <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-sm font-medium">{t('bookings.selected')}: <span className="text-primary">{selectedElement.expense_type}</span> ({selectedElement.element_id})</p>
                    <p className="text-xs text-muted-foreground">
                      {t('bookings.estimated')}: {selectedPackage.currency} {selectedElement.value?.toLocaleString()} | 
                      {t('bookings.alreadyBooked')}: {selectedPackage.currency} {selectedElement.total_booked?.toLocaleString()} ({selectedElement.booking_count} {t('bookings.bookingsCount').toLowerCase()})
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 3: Booking Details */}
          <Card>
            <CardHeader>
              <CardTitle>{selectedPackage?.use_bottom_up ? t('bookings.step3BookingDetails') : t('bookings.bookingDetails')}</CardTitle>
              <CardDescription>{t('bookings.enterBookingInfo')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="date_of_booking">{t('bookings.bookingDate')} *</Label>
                  <Input
                    id="date_of_booking"
                    type="date"
                    value={formData.date_of_booking}
                    onChange={(e) => setFormData({ ...formData, date_of_booking: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_service">{t('bookings.serviceDate')} *</Label>
                  <Input
                    id="date_of_service"
                    type="date"
                    value={formData.date_of_service}
                    onChange={(e) => setFormData({ ...formData, date_of_service: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="service_provider">{t('bookings.serviceProvider')} *</Label>
                <Input
                  id="service_provider"
                  value={formData.service_provider}
                  onChange={(e) => setFormData({ ...formData, service_provider: e.target.value })}
                  placeholder={t('bookings.enterServiceProvider')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="booking_cost" className="text-orange-600 font-semibold">
                  {t('bookings.bookingCost')} ({selectedPackage?.currency || 'EUR'}) *
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('bookings.bookingCostDescription')}
                </p>
                <Input
                  id="booking_cost"
                  type="number"
                  step="0.01"
                  value={formData.booking_cost}
                  onChange={(e) => setFormData({ ...formData, booking_cost: e.target.value })}
                  placeholder={t('bookings.enterBookingCost')}
                  required
                  className="border-orange-300 focus:border-orange-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">{t('bookings.paymentMethod')} *</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })} required>
                    <SelectTrigger>
                      <SelectValue placeholder={t('bookings.selectMethod')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Card">{t('bookings.card')}</SelectItem>
                      <SelectItem value="Bank Transfer">{t('bookings.bankTransfer')}</SelectItem>
                      <SelectItem value="Cash">{t('bookings.cash')}</SelectItem>
                      <SelectItem value="Invoice">{t('bookings.invoice')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_status">{t('bookings.paymentStatus')} *</Label>
                  <Select value={formData.payment_status} onValueChange={(value) => setFormData({ ...formData, payment_status: value })} required>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">{t('bookings.pending')}</SelectItem>
                      <SelectItem value="Paid">{t('bookings.paid')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.payment_status === 'Paid' && (
                <div className="space-y-2">
                  <Label htmlFor="payment_date">{t('bookings.paymentDate')} *</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    required
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex items-center gap-4">
            <Button 
              type="submit" 
              size="lg" 
              className="px-8"
              disabled={selectedPackage?.use_bottom_up && !selectedCostElementId}
            >
              {t('bookings.createBooking')}
            </Button>
            <Button type="button" variant="outline" size="lg" onClick={resetForm}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // Bookings List View
  return (
    <div className="space-y-8" data-testid="bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-extrabold text-foreground tracking-tight">{t('bookings.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('bookings.subtitle')}</p>
        </div>
        {canEdit('bookings') && (
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => setShowCreateForm(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('bookings.createBooking')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('bookings.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterPackageId} onValueChange={setFilterPackageId}>
              <SelectTrigger className="w-48">
                <Filter className="w-4 h-4 mr-1" />
                <SelectValue placeholder={t('bookings.filterByPackage')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('dashboard.allPackages')}</SelectItem>
                {packages.map(pkg => (
                  <SelectItem key={pkg.package_id} value={pkg.package_id}>
                    {pkg.package_id} - {pkg.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table - Reorganized columns */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-heading font-semibold">{t('bookings.allBookings')}</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBookings.length > 0 ? (
            <div className="w-full overflow-auto rounded-lg border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>{t('bookings.docStatus')}</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('package_id')}>
                      <div className="flex items-center gap-1">
                        {t('packages.packageId')}
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('booking_id')}>
                      <div className="flex items-center gap-1">
                        {t('bookings.bookingId')} / {t('bookings.transactionId')}
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead>{t('bookings.serviceProvider')}</TableHead>
                    <TableHead>{t('bookings.bookingDate')}</TableHead>
                    <TableHead>{t('bookings.serviceDate')}</TableHead>
                    <TableHead className="text-right">{t('bookings.estimatedCost')}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => handleSort('booking_cost')}>
                      <div className="flex items-center justify-end gap-1">
                        {t('bookings.actualCost')}
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>{t('bookings.paymentMethod')}</TableHead>
                    <TableHead>{t('bookings.paymentStatus')}</TableHead>
                    <TableHead>{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => {
                    const pkgInfo = getPackageInfo(booking.package_id);
                    const estimatedCost = Math.round((booking.planned_cost || booking.service_value || 0) * 100) / 100;
                    const actualCost = Math.round((booking.booking_cost || booking.actual_cost || booking.service_value || 0) * 100) / 100;
                    const amountPaid = Math.round((booking.supplier_amount_paid || 0) * 100) / 100;
                    const balanceDue = Math.round((booking.supplier_balance_due ?? (actualCost - amountPaid)) * 100) / 100;
                    const paymentStatus = booking.calculated_payment_status || booking.payment_status;
                    const currency = booking.currency || pkgInfo?.currency || 'EUR';
                    
                    return (
                      <TableRow key={booking.booking_id}>
                        <TableCell>
                          {getDocStatusIndicator(booking)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-mono text-sm font-medium">{booking.package_id}</p>
                            {pkgInfo && <p className="text-xs text-muted-foreground">{pkgInfo.client_name}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-mono text-sm">{booking.booking_id}</p>
                            <p className="font-mono text-xs text-muted-foreground">{booking.transaction_id}</p>
                          </div>
                        </TableCell>
                        <TableCell>{booking.service_provider}</TableCell>
                        <TableCell>{booking.date_of_booking}</TableCell>
                        <TableCell>{booking.date_of_service}</TableCell>
                        <TableCell className="text-right font-mono text-blue-600">
                          {currency} {estimatedCost.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {currency} {actualCost.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-green-600">
                          {amountPaid > 0 ? `${currency} ${amountPaid.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {balanceDue > 0 ? `${currency} ${balanceDue.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell>{booking.payment_method}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={`${
                              paymentStatus === 'Paid' ? 'bg-green-100 text-green-800 border-green-300' : 
                              paymentStatus === 'Partial' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : 
                              'bg-gray-100 text-gray-800 border-gray-300'
                            }`}
                          >
                            {paymentStatus === 'Paid' ? t('bookings.paid') : 
                             paymentStatus === 'Partial' ? 'Partial' : t('bookings.pending')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => setViewingBooking(booking)}
                              title="View Details & Payments"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canEdit('bookings') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleEditBooking(booking)}
                                title={t('bookings.editBooking')}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {canEdit('bookings') && paymentStatus !== 'Paid' && (
                              <Button size="sm" onClick={() => handleMarkPaidClick(booking.booking_id)}>
                                {t('bookings.markPaid')}
                              </Button>
                            )}
                            {canEdit('bookings') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleArchiveBooking(booking.booking_id)}
                                title={t('common.archive')}
                              >
                                <Archive className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            )}
                            {canEdit('bookings') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeleteBooking(booking.booking_id)}
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  
                  {/* Summary Row */}
                  <TableRow className="bg-slate-100 dark:bg-slate-800 font-semibold border-t-2">
                    <TableCell colSpan={6} className="text-right">
                      <span className="text-muted-foreground">Totals ({filteredBookings.length} bookings):</span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-blue-600">
                      {filteredBookings.length > 0 && (filteredBookings[0].currency || 'EUR')} {
                        Math.round(filteredBookings.reduce((sum, b) => sum + (b.planned_cost || b.service_value || 0), 0) * 100) / 100
                      }
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {filteredBookings.length > 0 && (filteredBookings[0].currency || 'EUR')} {
                        Math.round(filteredBookings.reduce((sum, b) => sum + (b.booking_cost || b.actual_cost || b.service_value || 0), 0) * 100) / 100
                      }
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-green-600">
                      {filteredBookings.length > 0 && (filteredBookings[0].currency || 'EUR')} {
                        Math.round(filteredBookings.reduce((sum, b) => sum + (b.supplier_amount_paid || 0), 0) * 100) / 100
                      }
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-red-600">
                      {filteredBookings.length > 0 && (filteredBookings[0].currency || 'EUR')} {
                        Math.round(filteredBookings.reduce((sum, b) => {
                          const actualCost = b.booking_cost || b.actual_cost || b.service_value || 0;
                          const paid = b.supplier_amount_paid || 0;
                          return sum + Math.max(0, actualCost - paid);
                        }, 0) * 100) / 100
                      }
                    </TableCell>
                    <TableCell>
                      <div className="text-xs space-y-1">
                        {Object.entries(
                          filteredBookings.reduce((acc, b) => {
                            const method = b.payment_method || 'Other';
                            acc[method] = (acc[method] || 0) + 1;
                            return acc;
                          }, {})
                        ).map(([method, count]) => (
                          <div key={method} className="flex justify-between">
                            <span>{method}:</span>
                            <Badge variant="outline" className="ml-2">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-green-600">Paid:</span>
                          <Badge className="ml-2 bg-green-100 text-green-800">
                            {filteredBookings.filter(b => (b.calculated_payment_status || b.payment_status) === 'Paid').length}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-yellow-600">Partial:</span>
                          <Badge className="ml-2 bg-yellow-100 text-yellow-800">
                            {filteredBookings.filter(b => (b.calculated_payment_status || b.payment_status) === 'Partial').length}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Pending:</span>
                          <Badge className="ml-2 bg-gray-100 text-gray-800">
                            {filteredBookings.filter(b => (b.calculated_payment_status || b.payment_status) === 'Pending').length}
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('bookings.noBookings')}</p>
              <p className="text-sm mt-2">
                {searchTerm || filterPackageId !== 'all' 
                  ? t('common.tryAdjustingFilters') 
                  : t('bookings.createFirstBooking')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Date Dialog */}
      <Dialog open={showPaymentDateDialog} onOpenChange={setShowPaymentDateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('bookings.markPaid')}</DialogTitle>
            <DialogDescription>
              {t('bookings.paymentDatePrompt')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="payment_date_input">{t('bookings.paymentDate')} *</Label>
            <Input
              id="payment_date_input"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="mt-2"
              required
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirmPayment}>
              {t('bookings.confirmPayment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('bookings.editBooking')}</DialogTitle>
            <DialogDescription>
              {t('bookings.updateBookingDetails')} {editingBooking?.booking_id}
            </DialogDescription>
          </DialogHeader>
          {editingBooking && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('bookings.bookingDate')}</Label>
                  <Input
                    type="date"
                    value={editingBooking.date_of_booking || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, date_of_booking: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('bookings.serviceDate')}</Label>
                  <Input
                    type="date"
                    value={editingBooking.date_of_service || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, date_of_service: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('bookings.serviceProvider')}</Label>
                <Input
                  value={editingBooking.service_provider || ''}
                  onChange={(e) => setEditingBooking({...editingBooking, service_provider: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('bookings.actualCost')} ({editingBooking.currency || 'EUR'})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingBooking.booking_cost || ''}
                  onChange={(e) => setEditingBooking({...editingBooking, booking_cost: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('bookings.paymentMethod')}</Label>
                  <Select 
                    value={editingBooking.payment_method || ''} 
                    onValueChange={(value) => setEditingBooking({...editingBooking, payment_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Card">{t('bookings.card')}</SelectItem>
                      <SelectItem value="Bank Transfer">{t('bookings.bankTransfer')}</SelectItem>
                      <SelectItem value="Cash">{t('bookings.cash')}</SelectItem>
                      <SelectItem value="Invoice">{t('bookings.invoice')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('bookings.paymentStatus')}</Label>
                  <Select 
                    value={editingBooking.payment_status || 'Pending'} 
                    onValueChange={(value) => setEditingBooking({...editingBooking, payment_status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">{t('bookings.pending')}</SelectItem>
                      <SelectItem value="Paid">{t('bookings.paid')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editingBooking.payment_status === 'Paid' && (
                <div className="space-y-2">
                  <Label>{t('bookings.paymentDate')}</Label>
                  <Input
                    type="date"
                    value={editingBooking.payment_date || ''}
                    onChange={(e) => setEditingBooking({...editingBooking, payment_date: e.target.value})}
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveEdit}>
              {t('bookings.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Booking Dialog with Payments */}
      <Dialog open={!!viewingBooking} onOpenChange={() => setViewingBooking(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <CalendarDays className="w-5 h-5" />
              Booking Details - {viewingBooking?.booking_id}
              <Badge variant={viewingBooking?.payment_status === 'Paid' ? 'default' : 'secondary'}>
                {viewingBooking?.payment_status}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {viewingBooking && (
            <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="details" className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Supplier Payments
                  {paymentSummary && (
                    <Badge variant="outline" className={`ml-1 ${getPaymentStatusColor(paymentSummary.payment_status)}`}>
                      {paymentSummary.payment_status}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              {/* Details Tab */}
              <TabsContent value="details" className="space-y-6">
                {/* Booking Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Service Provider</p>
                    <p className="font-semibold">{viewingBooking.service_provider || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Package</p>
                    <p className="font-semibold">{viewingBooking.package_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Date of Booking</p>
                    <p className="font-semibold">{viewingBooking.date_of_booking || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Date of Service</p>
                    <p className="font-semibold">{viewingBooking.date_of_service || '-'}</p>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground uppercase">Booking Cost</p>
                      <p className="text-2xl font-bold">
                        {viewingBooking.currency || 'EUR'} {(viewingBooking.booking_cost || 0).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground uppercase">Payment Method</p>
                      <p className="text-lg font-semibold">{viewingBooking.payment_method || '-'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground uppercase">Payment Status</p>
                      <Badge className={`mt-1 ${getPaymentStatusColor(viewingBooking.payment_status)}`}>
                        {viewingBooking.payment_status}
                      </Badge>
                    </CardContent>
                  </Card>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  {canEdit('bookings') && (
                    <Button onClick={() => { setViewingBooking(null); handleEditBooking(viewingBooking); }}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Booking
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => setViewingBooking(null)}>
                    Close
                  </Button>
                </div>
              </TabsContent>
              
              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-6">
                {loadingPayments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Loading payments...
                  </div>
                ) : (
                  <>
                    {/* Payment Summary Cards */}
                    <div className="grid grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-4">
                          <p className="text-xs text-muted-foreground uppercase">Total Cost</p>
                          <p className="text-2xl font-bold">
                            {paymentSummary?.currency || viewingBooking.currency || 'EUR'} {(paymentSummary?.total_amount || viewingBooking.booking_cost || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-green-600 uppercase">Paid to Supplier</p>
                          <p className="text-2xl font-bold text-green-700">
                            {paymentSummary?.currency || viewingBooking.currency || 'EUR'} {(paymentSummary?.amount_paid || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-orange-50">
                        <CardContent className="pt-4">
                          <p className="text-xs text-orange-600 uppercase">Balance Due</p>
                          <p className="text-2xl font-bold text-orange-700">
                            {paymentSummary?.currency || viewingBooking.currency || 'EUR'} {(paymentSummary?.balance_due || viewingBooking.booking_cost || 0).toLocaleString()}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <p className="text-xs text-muted-foreground uppercase">Status</p>
                          <Badge className={`mt-2 ${getPaymentStatusColor(paymentSummary?.payment_status || 'Pending')}`}>
                            {paymentSummary?.payment_status || 'Pending'}
                          </Badge>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Add Payment Button */}
                    {canEdit('bookings') && (
                      <div className="flex justify-end">
                        <Button onClick={() => setShowAddPaymentDialog(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Record Payment to Supplier
                        </Button>
                      </div>
                    )}
                    
                    {/* Payment History */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Receipt className="w-5 h-5" />
                          Payment History (to Service Provider)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {/* Show note if booking was marked paid before new system */}
                        {paymentSummary?.old_payment_status === 'Paid' && paymentHistory.length === 0 && (
                          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-700">
                              <span className="font-semibold">✓ Marked as Fully Paid</span> - This booking was marked as paid before the detailed payment tracking was implemented.
                              {viewingBooking.payment_date && (
                                <span className="block mt-1">Payment Date: {viewingBooking.payment_date}</span>
                              )}
                            </p>
                          </div>
                        )}
                        
                        {paymentHistory.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Payment ID</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Method</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="w-20">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paymentHistory.map((payment) => (
                                <TableRow key={payment.payment_id}>
                                  <TableCell className="font-mono text-xs">{payment.payment_id}</TableCell>
                                  <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{payment.payment_method}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right font-mono font-semibold text-green-600">
                                    {viewingBooking.currency || 'EUR'} {payment.amount.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    {canEdit('bookings') && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        onClick={() => handleDeleteBookingPayment(payment.payment_id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : !paymentSummary?.old_payment_status || paymentSummary?.old_payment_status !== 'Paid' ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No payments recorded yet</p>
                            <p className="text-sm mt-2">Click &quot;Record Payment to Supplier&quot; to add a payment</p>
                          </div>
                        ) : null}
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Supplier Payment Dialog */}
      <Dialog open={showAddPaymentDialog} onOpenChange={setShowAddPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Record Payment to Service Provider
            </DialogTitle>
            <DialogDescription>
              Record a payment made to the service provider for this booking
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-9"
                />
              </div>
              {paymentSummary && (
                <p className="text-xs text-muted-foreground mt-1">
                  Balance due: {paymentSummary.currency || 'EUR'} {paymentSummary.balance_due?.toLocaleString()}
                </p>
              )}
            </div>
            
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Payment Method *</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddBookingPayment} disabled={addingPayment}>
              {addingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookings;
